
/* eslint-disable no-unused-expressions, @typescript-eslint/no-unused-vars, no-useless-return */
import { defineComponent, ref, onMounted } from 'vue'
import { IProps, EMode } from './type'
import { dateToISO, cloneDate, iResetDate } from '@/helper/date'

// https://vcalendar.io/api/v2.0/calendar.html#from-date
export default defineComponent({
    props: ['mode', 'availableDates', 'presetDate', 'callbackOnPickDate'],
    setup (props: IProps) {
        const lang = window.config.getLang()

        const activeDate = props.presetDate ? ref(props.presetDate) : ref()
        const availableDates = !props.availableDates ? [] : props.availableDates
        const minDate = availableDates[0]

        const callbackOnPickDate = props.callbackOnPickDate ? props.callbackOnPickDate : (date: Date|undefined) => { return }

        const activeDateHour = ref<Date|undefined>()
        const selectOptionsHours = ref()
        const activeDateMinute = ref<Date|undefined>()
        const selectOptionsMinutes = ref()

        onMounted(async () => {
            init()
        })

        /**
         * Vrací pole datumů(s přesností na hodiny) podle vybraného dne.
         * Prouze pro mód: date-time (EMode.dateTime)
         */
        const filterAvailableHours = (activeDate: Date|undefined, availableDates: Date[]): Date[] => {
            if (!activeDate || availableDates.length === 0) {
                return []
            }

            const dates = new Map()
            availableDates.filter((date) => {
                return dateToISO(date) === dateToISO(activeDate)
            }).forEach((date) => {
                const clone = cloneDate(date)
                clone.setMinutes(0)
                dates.set(clone.getHours(), clone)
            })

            return Array.from(dates.values())
        }

        /**
         * Vrací pole datumů(s přesností na minuty) podle vybraného dne a hodiny.
         * Prouze pro mód: date-time (EMode.dateTime)
         */
        const filterAvailableMinutes = (activeDate: Date|undefined, activeDateHour: Date|undefined, availableDates: Date[]): Date[] => {
            if (!activeDate || !activeDateHour || availableDates.length === 0) {
                return []
            }
            return availableDates.filter((date) => {
                return dateToISO(date) === dateToISO(activeDate) && date.getHours() === activeDateHour.getHours()
            })
        }

        /**
         * Set values do <select> pro hodiny
         * values selectu jsou objecty Date
         */
        const setHoursToSelect = (datesHours: Date[]) => {
            selectOptionsHours.value = []
            datesHours.forEach(date => {
                const hour = date.getHours()
                selectOptionsHours.value.push({
                    text: hour.toString().length === 1 ? `0${hour}` : hour,
                    value: date,
                })
            })
        }

        /**
         * Set values do <select> pro minuty
         * values selectu jsou objecty Date
         */
        const setMinutesToSelect = (dates: Date[]) => {
            selectOptionsMinutes.value = []
            dates.forEach(date => {
                const minute = date.getMinutes().toString()
                selectOptionsMinutes.value.push({
                    text: minute.length === 1 ? `0${minute}` : minute,
                    value: date,
                })
            })
        }

        /**
         * Setnutí první hodiny jako aktivní
         */
        const setActiveHour = async (dateHour: Date) => {
            const clone = cloneDate(dateHour)
            clone.setMinutes(0)
            activeDateHour.value = clone
        }

        /**
         * Setnutí první minuty jako aktivní
         */
        const setActiveMinute = async (dateMinute: Date) => {
            activeDateMinute.value = dateMinute
        }

        /**
         * Update hodin pro <select>
         */
        const updateSelectOptionsHours = async (isInit = false) => {
            const dateHours = filterAvailableHours(activeDate.value, availableDates)
            setHoursToSelect(dateHours)
            setActiveHour(isInit ? activeDate.value : dateHours[0])
        }

        /**
         * Update minut pro <select>
         */
        const updateSelectOptionsMinutes = async (isInit = false) => {
            const dateMinutes = filterAvailableMinutes(activeDate.value, activeDateHour.value, availableDates)
            setMinutesToSelect(dateMinutes)
            setActiveMinute(isInit ? activeDate.value : dateMinutes[0])
        }

        /**
         * Výběr datumu v kalendáři
         */
        const onDatePick = async (isInit = false) => {
            if (props.mode === EMode.dateTime) {
                if (!isInit) {
                    activeDate.value = iResetDate(activeDate.value)
                }
                await updateSelectOptionsHours(isInit)
                await updateSelectOptionsMinutes(isInit)
                if (!isInit) {
                    callbackOnPickDate(activeDateMinute.value)
                }
            }
        }

        /**
         * Výběr hodiny v <select>
         */
        const onHourPick = async () => {
            if (props.mode === EMode.dateTime) {
                await updateSelectOptionsMinutes()
                callbackOnPickDate(activeDateMinute.value)
            }
        }

        /**
         * Výběr minuty v <select>
         */
        const onMinutePick = async () => {
            if (props.mode === EMode.dateTime) {
                callbackOnPickDate(activeDateMinute.value)
            }
        }

        const init = async () => {
            if (props.mode === EMode.dateTime) {
                if (activeDate) {
                    onDatePick(true)
                }
            }
        }

        return {
            locale: lang === 'cz' ? 'cs' : lang,
            minDate,
            activeDate,
            activeDateHour,
            selectOptionsHours,
            activeDateMinute,
            selectOptionsMinutes,
            onDatePick,
            onHourPick,
            onMinutePick,
        }
    },
})
